import { useState } from 'react';
import { RunButton, InfoCard, NoResult, AiAdvisor } from './common';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';

const statusColor = (s = '') => (s === 'DUE NOW' ? 'bg-amber-100 text-amber-800'
    : s === 'Completed' ? 'bg-slate-100 text-slate-700' : 'bg-emerald-100 text-emerald-800');

const Nutrient = ({ selectedCrop, crops, year, season }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ph, setPh] = useState('6.5');
  const label = crops.find(c => c.key === selectedCrop)?.label;

  const run = async () => {
    setLoading(true);
    try { setResult(await api.nutrient({ crop_key: selectedCrop, year, season, field_ph: parseFloat(ph) || null })); }
    catch (e) { toast.error('Nutrient advisory failed'); }
    finally { setLoading(false); }
  };

  return (
    <InfoCard title={`Nutrient Recommendation · ${label}`}
              subtitle="PJTSAU / ICAR package-of-practices NPK schedule, keyed to days-after-sowing. Flags stages that are due now.">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="min-w-[160px]">
          <Label className="text-xs">Field soil pH (optional)</Label>
          <Input value={ph} onChange={e => setPh(e.target.value)} />
        </div>
        <RunButton testId={APP.actions.runNutrient} onClick={run} loading={loading}>Get schedule</RunButton>
      </div>

      {!result && !loading && <div className="mt-4"><NoResult /></div>}
      {result && (
        <div className="mt-5 space-y-4 animate-fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kv l="Sowing" v={result.sowing_date} />
            <Kv l="Harvest (est.)" v={result.harvest_date} />
            <Kv l="DAS today" v={result.das_today} />
            <Kv l="Total NPK (kg/ha)" v={`${result.npk_total.N}/${result.npk_total.P2O5}/${result.npk_total.K2O}`} />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stage</TableHead>
                <TableHead>DAS window</TableHead>
                <TableHead>Calendar</TableHead>
                <TableHead className="text-right">N</TableHead>
                <TableHead className="text-right">P2O5</TableHead>
                <TableHead className="text-right">K2O</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.schedule.map((r) => (
                <TableRow key={r.Stage}>
                  <TableCell className="font-medium">{r.Stage}</TableCell>
                  <TableCell>{r['DAS window']}</TableCell>
                  <TableCell className="text-xs">{r['Calendar window']}</TableCell>
                  <TableCell className="text-right">{r['N (kg/ha)']}</TableCell>
                  <TableCell className="text-right">{r['P2O5 (kg/ha)']}</TableCell>
                  <TableCell className="text-right">{r['K2O (kg/ha)']}</TableCell>
                  <TableCell><Badge className={statusColor(r.Status)}>{r.Status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="space-y-2">
            {result.schedule.map((r) => (
              <p key={`note-${r.Stage}`} className="text-xs text-muted-foreground"><b>{r.Stage}:</b> {r.Note}</p>
            ))}
          </div>

          {result.ph_note && (
            <div className="p-3 rounded-lg bg-icrisat-mustard/20 border border-icrisat-mustard/40 text-sm">
              <b>Soil pH note:</b> {result.ph_note}
            </div>
          )}
          <AiAdvisor context="nutrient" payload={result} />
        </div>
      )}
    </InfoCard>
  );
};

const Kv = ({ l, v }) => (
  <div className="rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-3">
    <div className="text-[10px] uppercase text-icrisat-earth">{l}</div>
    <div className="font-display text-icrisat-green">{v ?? '—'}</div>
  </div>
);

export default Nutrient;
